import { configureStore, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const NETLIFY_CONTACT_FORM = "contact";
const isLocalHost = () =>
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

const isNetlifyHost = () =>
  process.env.REACT_APP_USE_NETLIFY_FORMS === "true" ||
  (typeof window !== "undefined" && !isLocalHost());

const getContactPayload = ({ name, email, subject, message }) => ({
  name,
  email,
  subject,
  message,
});

const encodeForm = (data) =>
  new URLSearchParams({
    "form-name": NETLIFY_CONTACT_FORM,
    ...getContactPayload(data),
  }).toString();

/* ------------ Theme slice ------------ */
const themeSlice = createSlice({
  name: "theme",
  initialState: { mode: "dark" },
  reducers: {
    toggle: (state) => {
      state.mode = state.mode === "dark" ? "light" : "dark";
    },
    set: (state, action) => {
      state.mode = action.payload;
    },
  },
});

/* ------------ Navigation slice ------------ */
const navSlice = createSlice({
  name: "nav",
  initialState: { active: "home", commandOpen: false },
  reducers: {
    setActive: (state, action) => {
      state.active = action.payload;
    },
    openCommand: (state) => {
      state.commandOpen = true;
    },
    closeCommand: (state) => {
      state.commandOpen = false;
    },
    toggleCommand: (state) => {
      state.commandOpen = !state.commandOpen;
    },
  },
});

/* ------------ Contact slice ------------ */
const contactSlice = createSlice({
  name: "contact",
  initialState: {
    submitting: false,
    success: false,
    error: null,
  },
  reducers: {
    submitStart: (state) => {
      state.submitting = true;
      state.success = false;
      state.error = null;
    },
    submitSuccess: (state) => {
      state.submitting = false;
      state.success = true;
      state.error = null;
    },
    submitFail: (state, action) => {
      state.submitting = false;
      state.success = false;
      state.error = action.payload || "Something went wrong";
    },
    resetContact: (state) => {
      state.submitting = false;
      state.success = false;
      state.error = null;
    },
  },
});

/* ------------ Visitor slice ------------ */
const visitorSlice = createSlice({
  name: "visitor",
  initialState: { count: 0, loaded: false },
  reducers: {
    setCount: (state, action) => {
      state.count = action.payload;
      state.loaded = true;
    },
  },
});

/* ------------ Thunks ------------ */
export const sendContactMessage = (payload) => async (dispatch) => {
  dispatch(contactSlice.actions.submitStart());
  try {
    if (isNetlifyHost()) {
      await axios.post("/", encodeForm(payload), {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
    } else {
      await axios.post(`${API}/contact`, getContactPayload(payload));
    }
    dispatch(contactSlice.actions.submitSuccess());
    return true;
  } catch (err) {
    const msg =
      err?.response?.data?.detail?.[0]?.msg ||
      err?.response?.data?.detail ||
      err?.message ||
      "Failed to send message";
    dispatch(contactSlice.actions.submitFail(String(msg)));
    return false;
  }
};

export const trackVisitor = () => async (dispatch) => {
  try {
    const visited = sessionStorage.getItem("st_visited");
    let res;
    if (visited) {
      res = await axios.get(`${API}/visitors`);
    } else {
      res = await axios.post(`${API}/visitors/increment`);
      sessionStorage.setItem("st_visited", "1");
    }
    dispatch(visitorSlice.actions.setCount(res.data?.count || 0));
  } catch (e) {
    // silent fail
  }
};

export const themeActions = themeSlice.actions;
export const navActions = navSlice.actions;
export const contactActions = contactSlice.actions;
export const visitorActions = visitorSlice.actions;

export const store = configureStore({
  reducer: {
    theme: themeSlice.reducer,
    nav: navSlice.reducer,
    contact: contactSlice.reducer,
    visitor: visitorSlice.reducer,
  },
});
